import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchQboConnectionStatus, startQboOAuth, disconnectQbo, updateQboSyncSettings, syncPendingInvoices, syncPendingPayments, testQboConnection } from '../../store/thunks/qboThunk';
import { setQboSyncSettings } from '../../store/slices/qboSlice';
import debounce from 'lodash/debounce';
import ConfirmDialog from '../../applicationUi/components/ConfirmDialog';
import { toast } from 'react-toastify';

function ClinicQuickbooks({ clinic }) {
  const dispatch = useDispatch();
  const isConnected = useSelector((state) => state.qbo.isConnected);
  const status = useSelector((state) => state.qbo.status);
  const realmId = useSelector((state) => state.qbo.realmId);
  const companyName = useSelector((state) => state.qbo.companyName);
  const syncSettings = useSelector((state) => state.qbo.syncSettings) || {
    autoSyncInvoices: false,
    autoSyncPayments: false,
    autoSyncCustomers: false,
    syncFrequency: 'daily'
  };
  const [isUpdating, setIsUpdating] = useState(false);
  const [localEmail, setLocalEmail] = useState(syncSettings.notificationEmail || '');
  const [showDisconnectDialog, setShowDisconnectDialog] = useState(false);

  useEffect(() => {
    if (clinic?._id) {
      dispatch(fetchQboConnectionStatus(clinic._id));
    }
  }, [dispatch, clinic]);

  // Update local email when syncSettings changes
  useEffect(() => {
    setLocalEmail(syncSettings.notificationEmail || '');
  }, [syncSettings.notificationEmail]);

  const handleConnect = async () => {
    if (!clinic?._id) return;
    dispatch(startQboOAuth(clinic._id));
  };

  const handleDisconnectClick = () => {
    if (!clinic?._id) return;
    setShowDisconnectDialog(true);
  };

  const handleDisconnectConfirm = async () => {
    setShowDisconnectDialog(false);
    dispatch(disconnectQbo(clinic._id));
  };

  const handleDisconnectCancel = () => {
    setShowDisconnectDialog(false);
  };

  const handleSettingChange = async (setting, value) => {
    if (!clinic?._id || !isConnected) return;
    
    setIsUpdating(true);
    try {
      const updatedSettings = { ...syncSettings, [setting]: value };
      const result = await dispatch(updateQboSyncSettings({ 
        clinicId: clinic._id, 
        settings: updatedSettings 
      })).unwrap();
      
      if (result?.syncSettings) {
        dispatch(setQboSyncSettings(result.syncSettings));
      }
    } catch (error) {
      console.error('Error updating setting:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  // Debounced email update function
  const debouncedEmailUpdate = useCallback(
    debounce(async (email) => {
      if (!clinic?._id || !isConnected) return;
      
      setIsUpdating(true);
      try {
        const updatedSettings = { ...syncSettings, notificationEmail: email };
        const result = await dispatch(updateQboSyncSettings({ 
          clinicId: clinic._id, 
          settings: updatedSettings 
        })).unwrap();
        
        if (result?.syncSettings) {
          dispatch(setQboSyncSettings(result.syncSettings));
        }
      } catch (error) {
        console.error('Error updating email:', error);
      } finally {
        setIsUpdating(false);
      }
    }, 1000),
    [clinic?._id, isConnected, syncSettings, dispatch]
  );

  const handleEmailChange = (e) => {
    const newEmail = e.target.value;
    setLocalEmail(newEmail);
    debouncedEmailUpdate(newEmail);
  };

  const handleManualSync = async (syncType) => {
    if (!clinic?._id || !isConnected) return;
    
    setIsUpdating(true);
    try {
      let result;
      switch (syncType) {
        case 'invoices':
          result = await dispatch(syncPendingInvoices(clinic._id)).unwrap();
          break;
        case 'payments':
          result = await dispatch(syncPendingPayments(clinic._id)).unwrap();
          break;
        case 'test':
          result = await dispatch(testQboConnection(clinic._id)).unwrap();
          // If the connection was auto-disconnected due to expired token, refresh the connection status
          if (result.autoDisconnected) {
            console.log('[QuickBooks Test] Connection was auto-disconnected, refreshing status');
            toast.warning('QuickBooks connection was expired and has been automatically disconnected. Please reconnect to resume synchronization.');
            dispatch(fetchQboConnectionStatus(clinic._id));
          }
          break;
        default:
          throw new Error('Invalid sync type');
      }
      console.log(`${syncType} sync completed:`, result);
    } catch (error) {
      console.error(`Error during ${syncType} sync:`, error);
    } finally {
      setIsUpdating(false);
    }
  };

  if (!clinic) {
    return (
      <div className="p-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
          <p className="text-yellow-800">Please select a clinic to manage QuickBooks integration.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900">QuickBooks Integration</h2>
        <p className="text-sm text-gray-600 mt-1">
          Manage QuickBooks Online integration for <span className="font-medium">{clinic.name}</span>
        </p>
      </div>
      
      <div className="bg-white rounded-lg shadow border border-gray-200">
        {/* Connection Status Section */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <div className="flex items-center space-x-3">
                <div className={`w-3 h-3 rounded-full ${
                  status === 'loading' ? 'bg-yellow-400 animate-pulse' :
                  isConnected ? 'bg-green-400' : 'bg-gray-400'
                }`}></div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Connection Status</h3>
                  <p className="text-gray-600">
                    {status === 'loading' ? 'Checking connection...' : 
                     isConnected ? 'Connected to QuickBooks Online' : 'Not connected to QuickBooks'}
                  </p>
                </div>
              </div>
              
              {isConnected && (
                <div className="mt-3 space-y-1">
                  {companyName && (
                    <p className="text-sm text-gray-600">
                      <span className="font-medium">Company:</span> {companyName}
                    </p>
                  )}
                  {realmId && (
                    <p className="text-sm text-gray-600">
                      <span className="font-medium">Company ID:</span> {realmId}
                    </p>
                  )}
                </div>
              )}
            </div>
            
            <button
              onClick={isConnected ? handleDisconnectClick : handleConnect}
              disabled={status === 'loading'}
              className={`px-6 py-2 rounded-md font-medium transition-colors ${
                isConnected 
                  ? 'bg-red-600 hover:bg-red-700 text-white'
                  : 'bg-blue-600 hover:bg-blue-700 text-white'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {status === 'loading' ? 'Processing...' : isConnected ? 'Disconnect' : 'Connect to QuickBooks'}
            </button>
          </div>
        </div>

        {/* Sync Settings Section */}
        {isConnected && (
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-medium text-gray-900">Sync Settings</h3>
              {isUpdating && (
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                  <span className="text-sm text-gray-600">Updating...</span>
                </div>
              )}
            </div>
            
            <div className="space-y-6">
              {/* Auto-sync Invoices */}
              <div className="flex items-center justify-between py-4 border-b border-gray-100">
                <div className="flex-1">
                  <p className="font-medium text-gray-900">Auto-sync invoices</p>
                  <p className="text-sm text-gray-600 mt-1">
                    Automatically sync new invoices to QuickBooks when created
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input 
                    type="checkbox" 
                    className="sr-only peer"
                    checked={syncSettings.autoSyncInvoices}
                    onChange={(e) => handleSettingChange('autoSyncInvoices', e.target.checked)}
                    disabled={isUpdating}
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              {/* Auto-sync Payments */}
              <div className="flex items-center justify-between py-4 border-b border-gray-100">
                <div className="flex-1">
                  <p className="font-medium text-gray-900">Auto-sync payments</p>
                  <p className="text-sm text-gray-600 mt-1">
                    Automatically sync new payments to QuickBooks when received
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input 
                    type="checkbox" 
                    className="sr-only peer"
                    checked={syncSettings.autoSyncPayments}
                    onChange={(e) => handleSettingChange('autoSyncPayments', e.target.checked)}
                    disabled={isUpdating}
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              {/* Auto-sync Customers */}
              <div className="flex items-center justify-between py-4 border-b border-gray-100">
                <div className="flex-1">
                  <p className="font-medium text-gray-900">Auto-sync customers</p>
                  <p className="text-sm text-gray-600 mt-1">
                    Automatically create QuickBooks customers from clinic clients
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input 
                    type="checkbox" 
                    className="sr-only peer"
                    checked={syncSettings.autoSyncCustomers}
                    onChange={(e) => handleSettingChange('autoSyncCustomers', e.target.checked)}
                    disabled={isUpdating}
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>
              
              {/* Sync Frequency */}
              <div className="flex items-center justify-between py-4">
                <div className="flex-1">
                  <p className="font-medium text-gray-900">Sync frequency</p>
                  <p className="text-sm text-gray-600 mt-1">
                    How often to check for and sync pending data. Live sync processes data immediately when changes occur.
                  </p>
                </div>
                <select 
                  className="border border-gray-300 rounded-md px-3 py-2 pr-8 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={syncSettings.syncFrequency}
                  onChange={(e) => handleSettingChange('syncFrequency', e.target.value)}
                  disabled={isUpdating}
                >
                  <option value="manual">Manual only</option>
                  <option value="live">Live (real-time)</option>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                </select>
              </div>
            </div>

            {/* Manual Sync Actions */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <h4 className="text-base font-medium text-gray-900 mb-4">Manual Actions</h4>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={() => handleManualSync('invoices')}
                  className="px-4 py-2 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 focus:ring-2 focus:ring-blue-500"
                  disabled={isUpdating}
                >
                  Sync Pending Invoices
                </button>
                <button
                  onClick={() => handleManualSync('payments')}
                  className="px-4 py-2 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 focus:ring-2 focus:ring-blue-500"
                  disabled={isUpdating}
                >
                  Sync Pending Payments
                </button>
                <button
                  onClick={() => handleManualSync('test')}
                  className="px-4 py-2 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 focus:ring-2 focus:ring-blue-500"
                  disabled={isUpdating}
                >
                  Test Connection
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Help Section */}
        {!isConnected && (
          <div className="p-6 bg-gray-50 border-t border-gray-200">
            <h4 className="text-base font-medium text-gray-900 mb-2">Getting Started</h4>
            <div className="text-sm text-gray-600 space-y-2">
              <p>
                Connect this clinic to QuickBooks Online to automatically sync invoices, payments, and customer data.
              </p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Each clinic can connect to a different QuickBooks company</li>
                <li>You'll need QuickBooks Online admin access to authorize the connection</li>
                <li>All sync settings are configured per clinic</li>
              </ul>
            </div>
          </div>
        )}
      </div>

      <ConfirmDialog
        isOpen={showDisconnectDialog}
        onClose={handleDisconnectCancel}
        onConfirm={handleDisconnectConfirm}
        title="Disconnect QuickBooks"
        message="Are you sure you want to disconnect QuickBooks? This will remove all sync settings for this clinic and stop automatic synchronization."
        confirmText="Disconnect"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  );
}

export default ClinicQuickbooks;